import type {
  Point,
  TransformState,
} from "../../../../../../types/stage-types.js";

/**
 * Origin tick configuration options
 */
export interface OriginTickConfig {
  readonly visible?: boolean;
  readonly size?: number;
  readonly color?: string;
  readonly lineWidth?: number;
}

/**
 * Origin tick state interface
 */
interface OriginTickState {
  readonly visible: boolean;
  readonly currentTransform: TransformState;
}

/**
 * OriginTickManager API for controlling origin tick display
 */
export interface OriginTickManagerAPI {
  readonly setVisible: (visible: boolean) => void;
  readonly updateTransform: (transform: TransformState) => void;
  readonly drawAt: (center: Point) => void;
  readonly render: () => void;
  readonly getState: () => Readonly<OriginTickState>;
  readonly destroy: () => void;
}

/**
 * Creates an OriginTickManager for handling origin tick mark display
 * @param container - Container element for the origin tick canvas
 * @param config - Origin tick configuration options
 * @returns OriginTickManager API
 */
export const createOriginTickManager = (
  container: HTMLElement,
  config: OriginTickConfig = {}
): OriginTickManagerAPI => {
  console.log("🎯 Creating OriginTickManager");

  let isDestroyed = false;

  // Configuration with defaults
  const tickConfig = {
    visible: config.visible !== false, // Default to true
    size: config.size || 6, // Half of 12px total size
    color: config.color || "#ff0000", // Red for visibility
    lineWidth: config.lineWidth || 1,
  };

  // Origin tick state
  let state: OriginTickState = {
    visible: tickConfig.visible,
    currentTransform: {
      scale: 1,
      translateX: 0,
      translateY: 0,
      rotation: 0,
    },
  };

  // Canvas for origin tick rendering
  let tickCanvas: HTMLCanvasElement | null = null;
  let tickCtx: CanvasRenderingContext2D | null = null;

  /**
   * Create and setup the origin tick canvas
   */
  const createTickCanvas = (): void => {
    if (tickCanvas) return; // Already created

    tickCanvas = document.createElement("canvas");
    tickCtx = tickCanvas.getContext("2d");

    if (!tickCtx) {
      console.error("Failed to get 2D context for origin tick canvas");
      return;
    }

    tickCanvas.className = "origin-tick-manager__canvas";
    tickCanvas.style.position = "absolute";
    tickCanvas.style.top = "0";
    tickCanvas.style.left = "0";
    tickCanvas.style.pointerEvents = "none";
    tickCanvas.style.zIndex = "10"; // Above other elements

    container.appendChild(tickCanvas);
  };

  /**
   * Setup canvas with retina support for crisp rendering
   */
  const setupRetinaCanvas = (width: number, height: number): void => {
    if (!tickCanvas || !tickCtx) return;

    const devicePixelRatio = window.devicePixelRatio || 1;

    // Set actual canvas size (retina resolution)
    tickCanvas.width = width * devicePixelRatio;
    tickCanvas.height = height * devicePixelRatio;

    // Scale canvas back down using CSS
    tickCanvas.style.width = width + "px";
    tickCanvas.style.height = height + "px";

    // Scale the drawing context
    tickCtx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    tickCtx.imageSmoothingEnabled = false;
  };

  /**
   * Calculate the coordinate origin based on product container position
   */
  const calculateCoordinateOrigin = (): Point => {
    const productContainer = document.querySelector(
      ".stage__product-container"
    ) as HTMLElement;

    if (!productContainer) {
      return {
        x: state.currentTransform.translateX,
        y: state.currentTransform.translateY,
      };
    }

    const containerRect = productContainer.getBoundingClientRect();
    const stageRect = container.getBoundingClientRect();

    const containerLeft = containerRect.left - stageRect.left;
    const containerTop = containerRect.top - stageRect.top;

    return {
      x: containerLeft / state.currentTransform.scale,
      y: containerTop / state.currentTransform.scale,
    };
  };

  /**
   * Draw origin tick mark at specified or calculated position
   * @param center - Center point for the tick mark (optional, defaults to calculated origin)
   */
  const drawOriginTick = (center?: Point): void => {
    if (isDestroyed || !tickCanvas || !tickCtx || !state.visible) return;

    let tickCenter: Point;

    if (center) {
      // Use provided center
      tickCenter = center;
    } else {
      // Calculate origin based on product container position
      const origin = calculateCoordinateOrigin();
      tickCenter = {
        x: origin.x * state.currentTransform.scale,
        y: origin.y * state.currentTransform.scale,
      };
    }

    // Store original context properties
    const originalLineWidth = tickCtx.lineWidth;
    const originalStrokeStyle = tickCtx.strokeStyle;
    const originalLineCap = tickCtx.lineCap;

    // Set tick mark properties
    tickCtx.lineWidth = tickConfig.lineWidth;
    tickCtx.strokeStyle = tickConfig.color;
    tickCtx.lineCap = "square"; // Sharp ends for tick mark

    tickCtx.beginPath();

    // Draw horizontal line
    tickCtx.moveTo(tickCenter.x - tickConfig.size, tickCenter.y);
    tickCtx.lineTo(tickCenter.x + tickConfig.size, tickCenter.y);

    // Draw vertical line
    tickCtx.moveTo(tickCenter.x, tickCenter.y - tickConfig.size);
    tickCtx.lineTo(tickCenter.x, tickCenter.y + tickConfig.size);

    tickCtx.stroke();

    // Restore original properties
    tickCtx.lineWidth = originalLineWidth;
    tickCtx.strokeStyle = originalStrokeStyle;
    tickCtx.lineCap = originalLineCap;
  };

  /**
   * Main render function
   */
  const render = (): void => {
    if (isDestroyed) return;

    // Only render if visible
    if (!state.visible) {
      if (tickCanvas && tickCtx) {
        tickCtx.clearRect(0, 0, tickCanvas.width, tickCanvas.height);
      }
      return;
    }

    // Ensure canvas exists
    if (!tickCanvas) {
      createTickCanvas();
    }

    if (!tickCanvas || !tickCtx) return;

    // Get container dimensions
    const rect = container.getBoundingClientRect();
    const width = rect.width || 1000;
    const height = rect.height || 1000;

    // Setup canvas with retina support
    setupRetinaCanvas(width, height);

    // Clear canvas
    tickCtx.clearRect(0, 0, width, height);

    // Draw origin tick
    drawOriginTick();
  };

  /**
   * Set origin tick visibility
   */
  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;

    state = { ...state, visible };
    render();
  };

  /**
   * Update transform state
   */
  const updateTransform = (transform: TransformState): void => {
    if (isDestroyed) return;

    state = { ...state, currentTransform: { ...transform } };
    render();
  };

  /**
   * Draw origin tick at specific position
   */
  const drawAt = (center: Point): void => {
    if (isDestroyed) return;

    // Ensure canvas exists and is set up
    if (!tickCanvas) {
      createTickCanvas();
    }

    if (!tickCanvas || !tickCtx) return;

    // Setup canvas if needed
    const rect = container.getBoundingClientRect();
    const width = rect.width || 1000;
    const height = rect.height || 1000;
    setupRetinaCanvas(width, height);

    // Draw at specified position
    drawOriginTick(center);
  };

  /**
   * Get current state
   */
  const getState = (): Readonly<OriginTickState> => ({ ...state });

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    if (tickCanvas && tickCanvas.parentNode) {
      tickCanvas.parentNode.removeChild(tickCanvas);
    }

    tickCanvas = null;
    tickCtx = null;
  };

  // Initialize
  console.log("OriginTickManager initialized");

  return {
    setVisible,
    updateTransform,
    drawAt,
    render,
    getState,
    destroy,
  };
};
